import React from "react"
import Typography from "aldoo-ra/Typography"
import { formatMoney } from "aldoo-ra/Store/currency"
import { PlusIcon, PencilIcon, MagnifyingGlassIcon, SparklesIcon, BanknotesIcon } from "@heroicons/react/24/outline"

const PHASE_SLOTS = [
  { type: 'trial', icon: MagnifyingGlassIcon, label: 'Trial', color: 'bg-admin_success/10 border-admin_success/30' },
  { type: 'introductory', icon: SparklesIcon, label: 'Introductory', color: 'bg-admin_progress/10 border-admin_progress/50' },
  { type: 'regular', icon: BanknotesIcon, label: 'Regular', color: 'bg-admin_info_bg border-admin_info/30' }
]

/**
 * Reusable component to display phase slots
 * Matches the style from phase-slot-editor
 */
export default function PhaseSlotDisplay({ phases = [], onClick, compact = false, allowedPhases = ['trial', 'introductory', 'regular'] }) {
  const getPhaseForSlot = (slotType) => {
    return phases.find(p => p.phase_type === slotType)
  }

  // Filter phase slots based on allowedPhases
  const visibleSlots = PHASE_SLOTS.filter(slot => allowedPhases.includes(slot.type))

  const formatPrice = (amount, currency) => {
    if (amount === 0) return 'Free'
    // in the phase's own currency, as money is written in English ($9.99, €9.99, BGN 9.99)
    return formatMoney(amount, currency?.toUpperCase(), { locale: 'en' })
  }

  const formatDuration = (iterations, interval, intervalCount, phaseType) => {
    if (!iterations) return 'Ongoing'

    // For trial and introductory phases, show duration in days/weeks/months
    if (phaseType === 'trial' || phaseType === 'introductory') {
      const totalDays = iterations * intervalCount * (
        interval === 'day' ? 1 :
        interval === 'week' ? 7 :
        interval === 'month' ? 30 :
        interval === 'year' ? 365 : 1
      )

      if (totalDays === 1) return '1 day'
      if (totalDays === 7) return '1 week'
      if (totalDays === 14) return '2 weeks'
      if (totalDays === 30) return '1 month'
      if (totalDays === 60) return '2 months'
      if (totalDays === 90) return '3 months'
      if (totalDays === 365) return '1 year'

      // Default to days
      return `${totalDays} days`
    }

    // For regular phase, this shouldn't be called (will use formatRegularPrice)
    return 'Ongoing'
  }

  const formatRegularPrice = (amount, currency, interval, intervalCount) => {
    // If free, just show "Free" without interval
    if (amount === 0) {
      return 'Free'
    }

    const price = formatPrice(amount, currency)
    if (intervalCount === 1) {
      return `${price} / ${interval}`
    }
    return `${price} / ${intervalCount} ${interval}s`
  }

  return (
    <div className="flex items-center gap-4 w-full">
      {visibleSlots.map((slot, index) => {
        const phase = getPhaseForSlot(slot.type)
        const isConfigured = !!phase

        return (
          <React.Fragment key={slot.type}>
            <button
              onClick={() => onClick?.(slot)}
              disabled={!onClick}
              className={`
                relative border-2 ${isConfigured ? 'border-solid' : 'border-dashed'} rounded-lg p-6 transition-all duration-200
                hover:border-solid hover:shadow-md cursor-pointer flex-1 min-h-[180px]
                ${isConfigured
                  ? `${slot.color} border-solid`
                  : 'bg-admin_canvas border-admin_bordered hover:bg-admin_paper'
                }
              `}
            >
              {/* Icon and label */}
              <div className="text-center mb-3">
                <div className="flex justify-center mb-2">
                  <slot.icon className="w-10 h-10 text-admin_text_secondary" />
                </div>

                <Typography 
                  variant="admin_label" 
                  className="font-semibold text-admin_text my-0"
                >
                  {slot.label}
                </Typography>
              </div>

              {/* Phase details or add button */}
              {isConfigured ? (
                <div className="text-center min-h-[60px] flex flex-col items-center justify-center">
                  {phase.enabled === false ? (
                    // Show "No Trial" or "No Intro" for disabled phases
                    <Typography 
                      variant="admin_txt1_dim" 
                      className="font-medium"
                    >
                      {slot.type === 'trial' ? 'No Trial' : slot.type === 'introductory' ? 'No Intro' : 'Disabled'}
                    </Typography>
                  ) : slot.type === 'regular' ? (
                    // Regular phase: show price and duration like trial/introductory
                    <>
                      <Typography
                        variant="admin_h4"
                        className="font-bold text-admin_text mb-1"
                      >
                        {formatPrice(phase.amount, phase.currency)}
                      </Typography>

                      {phase.interval && (
                        <Typography variant="admin_txt2_dim">
                          {phase.interval_count === 1 ? phase.interval : `${phase.interval_count} ${phase.interval}s`}
                        </Typography>
                      )}
                    </>
                  ) : (
                    // Trial/Introductory: show price and duration
                    <>
                      <Typography
                        variant="admin_h4"
                        className="font-bold text-admin_text mb-1"
                      >
                        {formatPrice(phase.amount, phase.currency)}
                      </Typography>

                      <Typography variant="admin_txt2_dim">
                        {formatDuration(phase.iterations, phase.interval, phase.interval_count, slot.type)}
                      </Typography>
                    </>
                  )}
                  <div className="absolute top-2 right-2">
                    <PencilIcon className="w-4 h-4 text-admin_text_secondary" />
                  </div>
                </div>
              ) : (
                <div className="text-center min-h-[60px] flex flex-col items-center justify-center">
                  <PlusIcon className="w-6 h-6 mx-auto text-admin_text_secondary mb-1" />

                  <Typography 
                    variant="admin_txt2_dim"
                  >
                    Click to configure
                  </Typography>
                </div>
              )}
            </button>

            {/* Arrow between slots */}
            {index < visibleSlots.length - 1 && (
              <div className="flex-shrink-0 text-3xl text-admin_text_secondary font-light">
                →
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}
